import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import styled from 'styled-components'
import { withStyles } from '@material-ui/core/styles'

import Typography from '@material-ui/core/Typography'
import TypeLabel from 'components/atoms/TypeLabel'
import BlueCheckbox from 'components/atoms/BlueCheckbox'
import ExpandTableBox from 'components/molecules/ExpandTableBox'
import LocationButton from 'components/atoms/buttons/LocationButton'
import TableTrapUserCell from './TableTrapUserCell'
import userIcon from 'assets/images/userIcon.png'

import { getPercentOf } from 'utils/formatting'
import {
  formatDate,
  LOCAL_DATE_FORMAT,
  LOCAL_TIME_DATE_FORMAT,
} from 'utils/date'

const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing(3)}px 0`,
    borderBottom: `1px solid ${theme.palette.background.medium}`,
  },
  interactable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundImage:
        'linear-gradient(to right, white 0%, #F5F7FA, 35%, #F5F7FA, 85%, white 100%)',
    },
  },
  column: {
    flexGrow: 1,
    display: 'flex',
    '& > p': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  inactive: {
    opacity: '30%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing(3)}px 0`,
    borderBottom: `1px solid ${theme.palette.background.medium}`,
  },
  error: {
    color: theme.palette.error.main,
  },
})

const Container = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`

const StyledAccountCircleIcon = styled.img.attrs({ src: userIcon })`
  width: 50px;
  height: 50px;
  color: #949494;
`

const StyledImg = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 25px;
`

const SubText = styled(Typography)`
  color: #949494;
`

class TableRow extends PureComponent {
  handleRowClick = () => {
    const { data, onClick } = this.props
    if (onClick) {
      onClick(data)
    }
  };

  getContent = (rowData, column) => {
    const {
      onItemSelect, classes, handleShare, data 
    } = this.props
    const content = rowData[column.field]

    const onSelectableClick = (event) => {
      event.stopPropagation()
      onItemSelect(rowData, column)
    }

    const onShareClick = () => {
      if (handleShare) {
        handleShare(data)
      }
    }
    const name = rowData.trapName || rowData.name || rowData.serial
    
    switch (column.format) {
      case 'profileType':
        return (
          <TypeLabel
            color={content === 'ADMIN' ? 'primary' : 'tertiary'}
            label={content}
            width={90}
          />
        )
      case 'trapUsers':
        const { trapUsers } = rowData
        return (
          <TableTrapUserCell users={trapUsers} handleShare={onShareClick} />
        )
      case 'nameEmail':
        const { email, fullName, state } = rowData
        return (
          <Container>
            <Typography variant="h4">{state === 'PENDING' ? 'Pending' : fullName || ''}</Typography>
            {email && <SubText >{email}</SubText>}
          </Container>
        )
      case 'displayPic':
        const { imageThumbUrl, firstName } = rowData
        return (
          imageThumbUrl ? <StyledImg src={imageThumbUrl} alt={`Avatar of ${firstName}`} /> : <StyledAccountCircleIcon />
        )
      case 'locate':
        return (
          <LocationButton
            style={{ marginLeft: 15 }}
            onClick={onSelectableClick}
          />
        )
      case 'groupName':
        return (
          <Typography className={content ? '' : classes.error}>
            {content || 'Unassigned'}
          </Typography>
        )
      case 'trapName':
        return (
          <Typography>
            {rowData.active ? 
              name : 
              rowData.historyInactiveAt ?
                `${name} (Archived at ${formatDate(rowData.historyInactiveAt, LOCAL_TIME_DATE_FORMAT)})` :
                name}
          </Typography>
        )
      case 'trapNameShort':
        return (
          <Typography>{`${name.substring(0, 25)} ${name.length > 25 ? '...' : ''
          } `}
          </Typography>
        )
      case 'percentage':
        return (
          <Typography>
            {getPercentOf(content, 100)}
          </Typography>
        )
      case 'trapsSyncedPercent':
        return (
          <Typography>
            {getPercentOf(rowData.totalTrapsSynced, rowData.totalTraps)}
          </Typography>
        )
      case 'fullDate':
        return (
          <Typography>{formatDate(content, LOCAL_DATE_FORMAT)}</Typography>
        )
      case 'timeDate':
        return (
          <Typography>{formatDate(content, LOCAL_TIME_DATE_FORMAT)}</Typography>
        )
      case 'checkbox':
        const { selectedItems, selectedItemKey } = this.props

        if (selectedItems && selectedItemKey) {
          const isActive = selectedItems.indexOf(rowData[selectedItemKey]) > -1

          return (
            <BlueCheckbox
              checked={isActive}
              className={classes.checkbox}
              onChange={onSelectableClick}
            />
          )
        }
        return <div />
      case 'expandBox':
        return (
          <ExpandTableBox
            onSelect={onSelectableClick}
            text={column.selectableLabel}
          />
        )
      case 'name':
      default:
        const placeholder = column.hidden ? '' : '-'
        return <Typography>{content || placeholder}</Typography>
    }
  };

  render() {
    const {
      classes, columns, data, isValid, onClick
    } = this.props

    const isInactiveTrap = data.trapUuid && !data.active

    return (
      <div
        onClick={isValid ? this.handleRowClick : null}
        className={classNames(
          isInactiveTrap ? classes.inactive : classes.root, 
          onClick && isValid && classes.interactable
        )}>
        {columns.map((column, index) => {
          return (
            <div
              key={index}
              style={{ maxWidth: column.width, justifyContent: column.align }}
              className={classes.column}>
              {this.getContent(data, column)}
            </div>
          )
        })}
      </div>
    )
  }
}

TableRow.propTypes = {
  data: PropTypes.object,
  onClick: PropTypes.func,
  isValid: PropTypes.bool,
  columns: PropTypes.array,
  classes: PropTypes.object,
  onItemSelect: PropTypes.func,
  selectedItems: PropTypes.array,
  selectedItemKey: PropTypes.string,
  handleShare: PropTypes.func,
}

export default withStyles(styles)(TableRow)



// WEBPACK FOOTER //
// ./src/components/atoms/TableRow.js